import * as React from 'react'
import { PotentiallyHtml } from '../PotentiallyHtml'
import {
  Accordion,
  Typography,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  TextareaAutosize,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'

import { useUpdateTreeNode } from './TreeRequests'

const styledGrid = {
  display: 'grid',
  gridTemplateColumns: 'min-content 1fr',
  gap: '0 .5rem',
}
export function EditableBodyView({
  node,
}: {
  node: {
    id: string
    tree_id: string
    version: string
    body?: { [key: string]: any }
  }
}) {
  const [editing, setEditing] = React.useState(false)
  if (editing) {
    return (
      <Box>
        <Button onClick={() => setEditing(false)}>Done</Button>
        <BodyEditView node={node} />
      </Box>
    )
  }
  return (
    <Box>
      <Button onClick={() => setEditing(true)}>Edit</Button>
      <BodyView body={node.body} />
    </Box>
  )
}
export function BodyView({ body }: { body?: { [key: string]: any } }) {
  const bodyValue = (value: any) => {
    // Parse Object into smaller bits. Add Accordion for sub-objects (like file attachments)
    if (value && typeof value === 'object') {
      return Object.keys(value).map((key) => {
        if (typeof value[key] === 'object') {
          return (
            <Accordion
              key={key}
              sx={{
                gridColumn: 'span 2',
                boxShadow: 'none',
                backgroundColor: 'inherit',
              }}
              disableGutters
              square
              data-testid="body-accordion"
            >
              <AccordionSummary
                sx={{
                  '& .MuiAccordionSummary-content': {
                    color: 'primary.main',
                    padding: '0',
                    margin: '0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'unset',
                  },
                  padding: '0',
                  minHeight: 'unset',
                }}
                expandIcon={<ExpandMore />}
                data-testid="body-accordion-summary"
              >
                <Typography>{key}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ ...styledGrid, overflowX: 'auto' }}>
                {bodyValue(value[key])}
              </AccordionDetails>
            </Accordion>
          )
        }
        return (
          <React.Fragment key={key}>
            <Typography data-testid="body-key">{key}:</Typography>
            <Typography data-testid="body-value">
              {bodyValue(value[key])}
            </Typography>
          </React.Fragment>
        )
      })
    }
    // Render in stringfied variable as raw html
    return <PotentiallyHtml s={`${value}`} />
  }

  return <div style={styledGrid}>{bodyValue(body)}</div>
}

function BodyEditView({
  node,
}: {
  node: {
    id: string
    tree_id: string
    version: string
    body?: { [key: string]: any }
  }
}) {
  const updater = useUpdateTreeNode({ showSuccess: true })
  const [bodyString, setBodyString] = React.useState('')
  const [err, setError] = React.useState('')

  React.useEffect(() => {
    setAndValidateBody(JSON.stringify(node.body))
  }, [node.body])

  function setAndValidateBody(newBody: string) {
    try {
      const parsed = JSON.parse(newBody)
      const asString = JSON.stringify(parsed, null, 2)
      setBodyString(asString)
      setError('')
    } catch (e: any) {
      setBodyString(newBody)
      setError(String(e))
    }
  }

  async function update() {
    try {
      const body = JSON.parse(bodyString)
      await updater({
        id: node.id,
        tree_id: node.tree_id,
        version: node.version,
        body,
      })
      node.body = body
    } catch (e: any) {
      setError(e)
    }
  }

  function buttonOnClick() {
    update().then(() => {})
  }

  return (
    <>
      <Button onClick={buttonOnClick}>Update</Button>
      <Box>
        <TextareaAutosize
          value={bodyString}
          style={{ width: '100%', overflow: 'auto' }}
          onChange={(e) => setAndValidateBody(e.target.value)}
        />
        {err && <Box>{err}</Box>}
      </Box>
    </>
  )
}
